import { grey, red } from "@ant-design/colors"
import { faCloudDownload, faInbox, faRotate } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ConfigProvider, Empty, Input, Row, Space, Table, Typography } from "antd"
import { useRef, useState } from "react"
import { NavigationService } from "../../services/navigationService"
import PageSidebar from "../common/PageSidebar"
import SidebarCreateButton from "../common/SidebarCreateButton"
import VSpace from "../common/VSpace"
import ThemableButton from "../Items/ThemableButton"
import { filterVendors, VendorItem } from "./vendorsModel"
import { useVendorsListQuery, useVendorsListQueryInvalidator } from "./vendorsQueries"
import ImportQuickBooksVendorsModal from "./ImportQuickBooksVendorsModal"

const { Text } = Typography

type Props = {
  showDeletedView: boolean
  selectedVendorId?: number
  onVendorSelect: (selectedVendorId: number) => void
  onCreate: () => void
  navigationService: NavigationService
}

const VendorsSidebar = ({ showDeletedView, selectedVendorId, onVendorSelect, onCreate }: Props) => {
  const vendorsListQuery = useVendorsListQuery()
  const invalidator = useVendorsListQueryInvalidator()
  const [search, setSearch] = useState('')
  const headerRef = useRef<HTMLDivElement>(null)
  const [showModal, setShowModal] = useState(false)

  const filteredVendors: VendorItem[] = filterVendors(vendorsListQuery.data ?? [], search)
  const headerHeight = headerRef.current?.clientHeight ?? 0

  const ImportVendorButton = () => <div
    className={'sidebar-catalog-item'}
    onClick={() => setShowModal(true)}
    style={{ cursor: 'pointer' }}
  >
    <Text type='secondary' style={{ display: 'block', color: '#ffffffd9' }}>
      Import Vendors
    </Text>
    <Text type="secondary" style={{ display: 'block', color: '#ffffff73' }}>
      <small>
        <Space>
          <FontAwesomeIcon icon={faCloudDownload} />
          Import from QuickBooks
        </Space>
      </small>
    </Text>
  </div>

  return <PageSidebar>
    <VSpace size="large">
      <ConfigProvider
        theme={{
          components: {
            Table: {
              colorBgContainer: '#3d587f',
              borderColor: '#3d587f',
              rowHoverBg: '#4a6b9a',
              colorText: 'white'
            }
          }
        }}
        renderEmpty={() =>
          <Empty
            description="No items found"
            image={<FontAwesomeIcon icon={faInbox} size="xl" />}
          />
        }
      >
        <div
          id="item-catalog-sidebar-scrollable-div"
          className="react-scrollable-list"
          style={{
            height: `calc(100vh - ${headerHeight}px - 24px)`,
          }}
        >
          <VSpace size="large">
            <VSpace style={{ padding: '10px 10px 0 10px' }}>
              <Row justify="space-between" align="middle">
                <Text style={{ color: 'white', fontSize: 18, fontFamily: 'Open Sans, sans-serif' }}>{showDeletedView ? 'DELETED ' : ''}Vendors</Text>
                <ThemableButton type="link" colorLink={grey[2]} icon={<FontAwesomeIcon icon={faRotate} />} onClick={invalidator} />
              </Row>
              <Row>
                <Input.Search placeholder="Search" value={search} onChange={x => setSearch(x.target.value ?? '')} allowClear />
              </Row>
            </VSpace>

            {!showDeletedView && <SidebarCreateButton text="Create New Vendor" onClick={onCreate} />}

            <div className="react-scrollable-list" style={{
              height: `calc(100vh - ${showDeletedView ? '110px' : '192px'})`
            }}>
              <ImportVendorButton />
              <ConfigProvider theme={{
                components: {
                  Table: {
                    colorBgContainer: '#3d587f',
                    borderColor: '#3d587f',
                    rowHoverBg: '#4a6b9a',
                    colorText: 'white'
                  }
                }
              }}>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={filteredVendors}
                  loading={vendorsListQuery.isFetching}
                  showHeader={false}
                  rowKey={x => x.id}
                  onRow={(vendor) => {
                    return {
                      style: {
                        cursor: 'pointer',
                        backgroundColor: selectedVendorId === vendor.id ? '#4a6b9a' : undefined,
                        color: showDeletedView ? red.primary : undefined
                      },
                      onClick: () => onVendorSelect(vendor.id)
                    }

                  }}
                  columns={[
                    {
                      render: (_, vendor) => <div>{vendor.name}</div>
                    }
                  ]}
                />
              </ConfigProvider>
            </div>
          </VSpace>
        </div>
      </ConfigProvider >
    </VSpace >
    <ImportQuickBooksVendorsModal
      onClose={() => setShowModal(false)}
      open={showModal}
    />
  </PageSidebar >
}

export default VendorsSidebar